/**
 * React Query hooks for Slack integration data fetching and mutations.
 * Follows the pattern from useJiraIntegration.ts with onSettled for invalidation.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getSlackIntegration,
  createSlackIntegration,
  updateSlackIntegration,
  deleteSlackIntegration,
  testSlackConnection,
} from "@/services/slackIntegrationService";
import { useApiReady } from "@/hooks/useApi";
import { ApiError } from "@/services/api";
import type {
  SlackIntegrationCreate,
  SlackIntegrationUpdate,
  SlackWebhookTestRequest,
} from "@/types/slackIntegration";

/**
 * Hook to fetch the current organization's Slack integration.
 * Returns null (not an error) when no integration is configured (404).
 */
export function useSlackIntegration() {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["slack-integration"],
    queryFn: async () => {
      try {
        return await getSlackIntegration();
      } catch (error) {
        // 404 means not configured — return null instead of throwing
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 30_000,
    enabled: isApiReady,
  });
}

/**
 * Hook to create a new Slack integration.
 * Invalidates the integration query on settled.
 */
export function useCreateSlackIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SlackIntegrationCreate) => createSlackIntegration(data),
    onSettled: (data, error) => {
      if (error) {
        toast.error("Failed to create Slack integration", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else if (data) {
        toast.success("Slack integration created", {
          description: data.channel_name
            ? `Connected to #${data.channel_name}`
            : "Webhook connected",
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["slack-integration"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to update the Slack integration.
 * Invalidates the integration query on settled.
 */
export function useUpdateSlackIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SlackIntegrationUpdate) => updateSlackIntegration(data),
    onSettled: (data, error) => {
      if (error) {
        toast.error("Failed to update Slack integration", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else if (data) {
        toast.success("Slack integration updated");
      }
      queryClient.invalidateQueries({
        queryKey: ["slack-integration"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to delete the Slack integration.
 * Invalidates the integration query on settled.
 */
export function useDeleteSlackIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteSlackIntegration(),
    onSettled: (_data, error) => {
      if (error) {
        toast.error("Failed to delete Slack integration", {
          description: error instanceof Error ? error.message : undefined,
        });
      } else {
        toast.success("Slack integration deleted");
      }
      queryClient.invalidateQueries({
        queryKey: ["slack-integration"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to test the Slack webhook connection.
 * Accepts optional inline credentials for pre-save testing.
 * No cache invalidation needed — this is a one-off test.
 */
export function useTestSlackConnection() {
  return useMutation({
    mutationFn: (credentials?: SlackWebhookTestRequest) =>
      testSlackConnection(credentials),
  });
}
